import { store } from '../store';
import { setGraphQLData } from '../graphqlSlice';
import { PricingMetric } from '../../components/company/types/metricTypes';

interface RootState {
  graphql: {
    data: any;
    loading: boolean;
    error: string | null;
  };
}

/**
 * Updates the Redux store after adding a new pricing metric
 */
export const addPricingMetricInRedux = (newMetric: PricingMetric & { page_id: string }): void => {
  try {
    // Get current state from Redux
    const state = store.getState() as RootState;
    const currentGraphQLData = state.graphql.data;
    
    if (!currentGraphQLData || !currentGraphQLData.pages || currentGraphQLData.pages.length === 0) {
      console.error('Cannot update Redux store: No GraphQL data available');
      return;
    }
    
    // Create a deep copy of the current GraphQL data to avoid direct state mutation
    const updatedGraphQLData = JSON.parse(JSON.stringify(currentGraphQLData));
    
    // Find the page where the metric was added
    const pageIndex = updatedGraphQLData.pages.findIndex((page: any) => page.id === newMetric.page_id);
    
    if (pageIndex === -1) {
      console.error(`Cannot update Redux store: Page ${newMetric.page_id} not found`);
      return;
    }
    
    // Initialize pricing_metrics array if it doesn't exist
    if (!updatedGraphQLData.pages[pageIndex].pricing_metrics) {
      updatedGraphQLData.pages[pageIndex].pricing_metrics = [];
    }
    
    // Add the new metric to the page
    updatedGraphQLData.pages[pageIndex].pricing_metrics.push({
      id: newMetric.id,
      display_name: newMetric.display_name,
      modality: newMetric.modality || null,
      sample_values: newMetric.sample_values || []
    });
    
    console.log('Adding pricing metric to Redux store:', newMetric);
    
    // Update the Redux store
    store.dispatch(setGraphQLData(updatedGraphQLData));
    
  } catch (error) {
    console.error('Error updating Redux store after adding pricing metric:', error);
  }
};

/**
 * Updates the Redux store after updating an existing pricing metric
 */
export const updatePricingMetricInRedux = (updatedMetric: PricingMetric & { page_id: string }): void => {
  try {
    // Get current state from Redux
    const state = store.getState() as RootState;
    const currentGraphQLData = state.graphql.data;
    
    if (!currentGraphQLData || !currentGraphQLData.pages || currentGraphQLData.pages.length === 0) {
      console.error('Cannot update Redux store: No GraphQL data available');
      return;
    }
    
    // Create a deep copy of the current GraphQL data to avoid direct state mutation
    const updatedGraphQLData = JSON.parse(JSON.stringify(currentGraphQLData));
    
    // Find the metric in all pages and update it
    let metricFound = false;
    
    for (let i = 0; i < updatedGraphQLData.pages.length; i++) {
      const page = updatedGraphQLData.pages[i];
      if (!page.pricing_metrics || !Array.isArray(page.pricing_metrics)) continue;
      
      const metricIndex = page.pricing_metrics.findIndex((m: any) => m.id === updatedMetric.id);
      
      if (metricIndex !== -1) {
        // If page_id has changed, remove from current page
        if (page.id !== updatedMetric.page_id) {
          page.pricing_metrics.splice(metricIndex, 1);
        } else {
          // Update the metric in place
          page.pricing_metrics[metricIndex] = {
            id: updatedMetric.id,
            display_name: updatedMetric.display_name,
            modality: updatedMetric.modality || null,
            sample_values: updatedMetric.sample_values || []
          };
          metricFound = true;
        }
      }
    }
    
    // If the page_id changed or metric wasn't found, add to the new page
    if (!metricFound) {
      const newPageIndex = updatedGraphQLData.pages.findIndex((p: any) => p.id === updatedMetric.page_id);
      
      if (newPageIndex !== -1) {
        if (!updatedGraphQLData.pages[newPageIndex].pricing_metrics) {
          updatedGraphQLData.pages[newPageIndex].pricing_metrics = [];
        }
        
        updatedGraphQLData.pages[newPageIndex].pricing_metrics.push({
          id: updatedMetric.id,
          display_name: updatedMetric.display_name,
          modality: updatedMetric.modality || null,
          sample_values: updatedMetric.sample_values || []
        });
        
        metricFound = true;
      }
    }
    
    if (!metricFound) {
      console.warn(`Could not find pricing metric with ID ${updatedMetric.id} to update`);
      return;
    }
    
    console.log('Updated pricing metric in Redux store:', updatedMetric);
    
    // Update the Redux store
    store.dispatch(setGraphQLData(updatedGraphQLData));
    
  } catch (error) {
    console.error('Error updating Redux store after updating pricing metric:', error);
  }
};

/**
 * Updates the Redux store after deleting a pricing metric
 */
export const removePricingMetricFromRedux = (metricId: string): void => {
  try {
    // Get current state from Redux
    const state = store.getState() as RootState;
    const currentGraphQLData = state.graphql.data;
    
    if (!currentGraphQLData || !currentGraphQLData.pages || currentGraphQLData.pages.length === 0) {
      console.error('Cannot update Redux store: No GraphQL data available');
      return;
    }
    
    // Create a deep copy of the current GraphQL data to avoid direct state mutation
    const updatedGraphQLData = JSON.parse(JSON.stringify(currentGraphQLData));
    
    // Find and remove the metric from all pages
    let metricFound = false;
    updatedGraphQLData.pages.forEach((page: any) => {
      if (page.pricing_metrics && Array.isArray(page.pricing_metrics)) {
        const initialLength = page.pricing_metrics.length;
        page.pricing_metrics = page.pricing_metrics.filter((metric: any) => metric.id !== metricId);
        if (page.pricing_metrics.length < initialLength) {
          metricFound = true;
        }
      }
    });
    
    if (!metricFound) {
      console.warn(`Pricing metric with ID ${metricId} not found in GraphQL data`);
      return;
    }
    
    console.log('Removed pricing metric from Redux store:', metricId);
    
    // Update the Redux store
    store.dispatch(setGraphQLData(updatedGraphQLData));
    
  } catch (error) {
    console.error('Error updating Redux store after removing pricing metric:', error);
  }
};
